import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useGetBoard, useListBoardWidgets, useWidgetData } from "@/hooks/useBoard"
import { ROUTES } from "@/routes/path"
import { TableWidget } from "./components/table-widget"
import { ChartWidget } from "./components/chart-widget"
import { NumberWidget } from "./components/number-widget"
import { TextWidget } from "./components/text-widget"
import Loader from "@/components/ui/loader"

export default function BoardWidgetDetail() {
  const { boardId, widgetId } = useParams<{ boardId: string; widgetId: string }>()
  const navigate = useNavigate()
  const { data: board } = useGetBoard(boardId || null)
  const { data: widgets, isLoading: widgetsLoading } = useListBoardWidgets(boardId || null)
  const { data: widgetData, isLoading: dataLoading } = useWidgetData(boardId || null, widgetId || null)

  const widget = widgets?.find((w) => w.id === widgetId)
  const isLoading = widgetsLoading || dataLoading

  if (!boardId || !widgetId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-gray-500">Invalid URL</p>
        <Button variant="link" onClick={() => navigate(ROUTES.DASHBOARD.BOARDS.LIST)}>
          Back to Boards
        </Button>
      </div>
    )
  }

  if (widgets && !widget) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-gray-500">Widget not found</p>
        <Button variant="outline" onClick={() => navigate(ROUTES.DASHBOARD.BOARDS.DETAIL(boardId))}>
          Back to Board
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-white dark:bg-[#0F0F12]">
      <div className="shrink-0 border-b border-gray-200 px-4 py-2.5 dark:border-gray-800">
        <div className="mx-auto flex max-w-[1920px] items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(ROUTES.DASHBOARD.BOARDS.DETAIL(boardId))}
            className="h-8 w-8"
            aria-label="Back to board"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-semibold text-gray-900 dark:text-white">
              {widget?.name ?? "Widget"}
            </h1>
            {board?.name && <p className="truncate text-xs text-gray-500 dark:text-gray-400">{board.name}</p>}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="flex h-full min-h-[400px] items-center justify-center">
            <Loader className="h-8 w-8" />
          </div>
        ) : widget ? (
          <div className="mx-auto h-full min-h-[400px] max-w-[1920px]">
            {widget.widget_type === "table" && <TableWidget data={widgetData} config={widget.config} />}
            {widget.widget_type === "chart" && <ChartWidget data={widgetData} config={widget.config} />}
            {widget.widget_type === "number" && <NumberWidget data={widgetData} config={widget.config} />}
            {widget.widget_type === "text" && <TextWidget data={widgetData} config={widget.config} />}
          </div>
        ) : null}
      </div>
    </div>
  )
}
